import { ArcadeAudio } from "./audio";
import { applyInput, createGame, loseLife, movePaddle, PADDLE_SPEED, stepGame, type GameInput, type GameState } from "./game";
import {
  createCombatState, resolveCombatState, resolvePlayerNames, type CombatState,
  type PlayerRole, type ServerMessage,
} from "./protocol";
import {
  advanceRockets, ballShieldCollision, createRocket, FIRE_COOLDOWN_SECONDS,
  MAX_ROCKETS_PER_PLAYER,
} from "./combat";

const canvas = document.querySelector<HTMLCanvasElement>("#court")!;
const context = canvas.getContext("2d")!;
const overlay = document.querySelector<HTMLElement>("#overlay")!;
const title = document.querySelector<HTMLElement>("#screen-title")!;
const message = document.querySelector<HTMLElement>("#screen-message")!;
const start = document.querySelector<HTMLButtonElement>("#start")!;
const playJay = document.querySelector<HTMLButtonElement>("#play-jay")!;
const createRoom = document.querySelector<HTMLButtonElement>("#create-room")!;
const stateNode = document.querySelector<HTMLElement>("#game-state")!;
const roomPanel = document.querySelector<HTMLElement>("#room-panel")!;
const roomStatus = document.querySelector<HTMLElement>("#room-status")!;
const roomLink = document.querySelector<HTMLInputElement>("#room-link")!;
const copyRoom = document.querySelector<HTMLButtonElement>("#copy-room")!;
const playerName = document.querySelector<HTMLInputElement>("#player-name")!;
const leftControl = document.querySelector<HTMLButtonElement>("#left")!;
const rightControl = document.querySelector<HTMLButtonElement>("#right")!;
const leftAction = document.querySelector<HTMLElement>("#left-action")!;
const rightAction = document.querySelector<HTMLElement>("#right-action")!;
const controlHint = document.querySelector<HTMLElement>("#control-hint")!;
const audio = new ArcadeAudio();
const PLAYER_NAME_KEY = "pong-player-name";

try {
  playerName.value = localStorage.getItem(PLAYER_NAME_KEY)?.slice(0, 12) ?? "";
} catch {
  // Storage can be unavailable in hardened/private browser contexts.
}
playerName.addEventListener("input", () => {
  try {
    if (playerName.value) localStorage.setItem(PLAYER_NAME_KEY, playerName.value.slice(0, 12));
    else localStorage.removeItem(PLAYER_NAME_KEY);
  } catch {
    // Name persistence is a convenience; joining still works without it.
  }
});

let game = createGame();
let networkTarget: GameState | null = null;
let previous = performance.now();
let held: -1 | 0 | 1 = 0;
let socket: WebSocket | null = null;
let roomCode = new URL(location.href).searchParams.get("room")?.toUpperCase().replace(/[^A-Z2-9]/g, "").slice(0, 6) ?? "";
let role: PlayerRole | "solo" = roomCode ? "spectator" : "solo";
let players = 0;
let names = { left: "PLAYER 1", right: "PLAYER 2" };
let connected = { left: false, right: false };
let canPlayJay = false;
let combat: CombatState = createCombatState();
let sequence = 0;
let firstNetworkState = true;
let reconnectTimer: number | null = null;
let soloLastFireAt = -Infinity;
let soloNextRocketId = 1;

function isMultiplayer(): boolean {
  return Boolean(roomCode);
}

function clientId(): string {
  const key = `pong-client-${roomCode}`;
  let value = sessionStorage.getItem(key);
  if (!value) {
    value = crypto.randomUUID();
    sessionStorage.setItem(key, value);
  }
  return value;
}

function send(value: object): void {
  if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(value));
}

function sendMove(direction: -1 | 0 | 1): void {
  if (role === "left" || role === "right") send({ type: "move", direction, seq: ++sequence });
}

function combatAction(direction: "left" | "right", active: boolean): void {
  if (role === "spectator") return;
  const leftSide = role === "left" || role === "solo";
  const toward = leftSide ? direction === "right" : direction === "left";
  if (toward) {
    if (!active) return;
    if (role === "solo") {
      const now = performance.now() / 1000;
      const rockets = combat.rockets.filter((rocket) => rocket.owner === "left").length;
      if (
        game.mode === "playing"
        && now - soloLastFireAt >= FIRE_COOLDOWN_SECONDS
        && rockets < MAX_ROCKETS_PER_PLAYER
      ) {
        combat.rockets.push(createRocket(game, "left", soloNextRocketId));
        soloNextRocketId += 1;
        soloLastFireAt = now;
      }
    } else send({ type: "fire", seq: ++sequence });
  } else {
    if (role === "solo") combat.shields.left = game.mode === "playing" && active;
    else send({ type: "shield", active, seq: ++sequence });
  }
  syncScreen();
}

function syncCombatControls(): void {
  const leftLabel = role === "right" ? "ROCKET" : "SHIELD";
  const rightLabel = role === "left" || role === "solo" ? "ROCKET" : "SHIELD";
  leftAction.textContent = leftLabel;
  rightAction.textContent = rightLabel;
  leftControl.setAttribute("aria-label", `LEFT ${leftLabel}`);
  rightControl.setAttribute("aria-label", `RIGHT ${rightLabel}`);
  controlHint.innerHTML = role === "left" || role === "right" || role === "solo"
    ? "<kbd>↑</kbd> <kbd>↓</kbd> MOVE · TOWARD FIRES · HOME SHIELDS"
    : "<kbd>↑</kbd> <kbd>↓</kbd> move · <kbd>Enter</kbd> OK · <kbd>Esc</kbd> exit twice for silence";
}

function connectToRoom(): void {
  if (!roomCode || socket?.readyState === WebSocket.OPEN || socket?.readyState === WebSocket.CONNECTING) return;
  if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
  reconnectTimer = null;
  const scheme = location.protocol === "https:" ? "wss:" : "ws:";
  const parameters = new URLSearchParams({ client: clientId() });
  if (playerName.value.trim()) parameters.set("name", playerName.value);
  socket = new WebSocket(`${scheme}//${location.host}/rooms/${roomCode}/ws?${parameters}`);
  roomStatus.textContent = `ROOM ${roomCode} · CONNECTING`;
  socket.addEventListener("message", (event) => receive(JSON.parse(String(event.data)) as ServerMessage));
  socket.addEventListener("close", () => {
    socket = null;
    if (!roomCode) return;
    players = Math.min(players, 1);
    canPlayJay = false;
    roomStatus.textContent = `ROOM ${roomCode} · RECONNECTING`;
    syncScreen();
    reconnectTimer = window.setTimeout(connectToRoom, 600);
  });
}

function receive(server: ServerMessage): void {
  if (server.type === "role") {
    role = server.role;
    document.body.dataset.role = role;
    roomStatus.textContent = `ROOM ${roomCode} · ${role.toUpperCase()}`;
    return;
  }
  if (server.type !== "state") return;
  players = server.players;
  // A room can briefly send the previous protocol shape during a rolling deploy.
  // Keep the safe local labels until a named snapshot arrives.
  names = resolvePlayerNames(server.names, names);
  connected = server.connected ?? connected;
  canPlayJay = server.canPlayJay ?? false;
  combat = resolveCombatState(server.combat);
  networkTarget = server.state;
  if (firstNetworkState) {
    game = { ...server.state };
    firstNetworkState = false;
  } else {
    const localY = role === "left" ? game.playerY : game.opponentY;
    game = { ...server.state };
    if (held && role === "left") game.playerY = localY;
    if (held && role === "right") game.opponentY = localY;
  }
  for (const event of server.events) audio.effect(event);
  syncScreen();
}

function beginRoom(code: string, joinUrl = `${location.origin}/?room=${code}`): void {
  roomCode = code;
  role = "spectator";
  players = 0;
  names = { left: "PLAYER 1", right: "PLAYER 2" };
  connected = { left: false, right: false };
  canPlayJay = false;
  combat = createCombatState();
  firstNetworkState = true;
  roomLink.value = joinUrl;
  history.replaceState(null, "", `/?room=${roomCode}`);
  roomPanel.hidden = false;
  createRoom.hidden = true;
  document.body.dataset.role = role;
  void audio.start();
  connectToRoom();
  syncScreen();
}

function leaveRoom(): void {
  if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
  reconnectTimer = null;
  roomCode = "";
  socket?.close(1000, "left room");
  socket = null;
  role = "solo";
  players = 0;
  connected = { left: false, right: false };
  canPlayJay = false;
  networkTarget = null;
  combat = createCombatState();
  game = createGame();
  roomPanel.hidden = true;
  createRoom.hidden = false;
  history.replaceState(null, "", "/");
  document.body.dataset.role = role;
  syncScreen();
}

function input(action: GameInput): void {
  if (isMultiplayer()) {
    if (action === "start") {
      void audio.start();
      connectToRoom();
      send({ type: "start", seq: ++sequence });
    } else if (action === "exit") {
      leaveRoom();
    } else if (action === "up" || action === "down") {
      const direction = action === "up" ? -1 : 1;
      sendMove(direction);
    }
    syncScreen();
    return;
  }
  if (action === "up" || action === "down") return;
  const oldMode = game.mode;
  game = applyInput(game, action);
  if (game.mode !== oldMode) {
    combat = createCombatState();
    soloLastFireAt = -Infinity;
    soloNextRocketId = 1;
  }
  if (game.mode === "stopped") audio.stop();
  if (action === "start" && game.mode === "playing" && oldMode !== "playing") void audio.start();
  syncScreen();
}

function syncScreen(): void {
  document.body.dataset.mode = game.mode;
  document.body.dataset.role = role;
  document.body.dataset.players = String(players);
  document.body.dataset.audio = game.mode === "stopped" ? "off" : (game.mode === "playing" ? "on" : document.body.dataset.audio ?? "off");
  stateNode.dataset.playerY = String(game.playerY);
  stateNode.dataset.opponentY = String(game.opponentY);
  stateNode.dataset.leftRockets = String(combat.rockets.filter((rocket) => rocket.owner === "left").length);
  stateNode.dataset.rightRockets = String(combat.rockets.filter((rocket) => rocket.owner === "right").length);
  stateNode.dataset.leftShield = String(combat.shields.left);
  stateNode.dataset.rightShield = String(combat.shields.right);
  stateNode.dataset.leftDamage = combat.damage.left.join(",");
  stateNode.dataset.rightDamage = combat.damage.right.join(",");
  syncCombatControls();
  const showPlayJay = isMultiplayer()
    && role === "left"
    && players === 1
    && canPlayJay;
  playJay.hidden = !showPlayJay;
  if (showPlayJay) {
    playJay.disabled = false;
    playJay.textContent = "PLAY AGAINST JAY";
  }
  stateNode.textContent = isMultiplayer()
    ? `${names.left} ${game.playerScore} · ${names.right} ${game.opponentScore}`
    : `YOU ${game.playerScore} · JAY ${game.opponentScore}`;

  if (isMultiplayer()) {
    roomPanel.hidden = false;
    createRoom.hidden = true;
    overlay.hidden = game.mode === "playing";
    if (players < 2) {
      const readyName = connected.left ? names.left : connected.right ? names.right : "";
      title.textContent = players ? "WAITING FOR FRIEND" : `ROOM ${roomCode}`;
      message.textContent = players
        ? canPlayJay
          ? `${readyName} IS READY · SEND THE LINK OR PLAY AGAINST JAY`
          : `${readyName} IS READY · SEND THEM THE LINK BELOW`
        : "PRESS OK TO CONNECT";
      start.textContent = players ? "OK · READY" : "OK · JOIN";
    } else if (game.mode === "gameover") {
      const winner = game.playerScore >= 5 ? names.right : names.left;
      title.textContent = `${winner} WINS`;
      message.textContent = `BALLS LOST ${names.left} ${game.playerScore} — ${names.right} ${game.opponentScore}`;
      start.textContent = "OK · PLAY AGAIN";
    } else if (game.mode === "paused") {
      title.textContent = "FRIEND DISCONNECTED";
      message.textContent = "THEIR PLACE IS SAVED · WAITING TO REJOIN";
      start.textContent = "OK · READY";
    }
    return;
  }

  overlay.hidden = game.mode === "playing" || game.mode === "paused";
  createRoom.hidden = false;
  if (game.mode === "title") {
    title.textContent = "JAY TV PONG";
    message.textContent = "UP / DOWN TO MOVE · 5 LOST BALLS LOSES";
    start.textContent = "OK · START";
  } else if (game.mode === "gameover") {
    title.textContent = game.winner;
    message.textContent = `${game.playerScore} — ${game.opponentScore} · BEST RALLY ${game.bestRally}`;
    start.textContent = "OK · PLAY AGAIN";
  } else if (game.mode === "stopped") {
    title.textContent = "JAY TV PONG";
    message.textContent = "STOPPED — SOUND OFF";
    start.textContent = "OK · START";
  }
}

function draw(state: GameState): void {
  const cellX = canvas.width / state.width;
  const cellY = canvas.height / state.height;
  context.fillStyle = "#030812";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.font = "bold 16px monospace";
  context.textAlign = "center";
  context.fillStyle = "#78f7a4";
  const labels = isMultiplayer() ? names.left : "YOU";
  const opponent = isMultiplayer() ? names.right : "JAY";
  context.fillText(`${labels}  ${state.playerScore}                 ${opponent}  ${state.opponentScore}`, canvas.width / 2, 17);
  context.fillStyle = "#1677ff";
  context.fillRect(0, cellY, canvas.width, 3);
  context.fillRect(0, canvas.height - 4, canvas.width, 3);
  context.fillStyle = "#7b8ba8";
  for (let y = 3; y < state.height - 1; y += 2) context.fillRect(canvas.width / 2 - 1, y * cellY, 3, cellY * 0.45);
  if (state.mode === "playing" || state.mode === "paused") {
    context.fillStyle = "#39e7ff";
    for (let segment = 0; segment < 5; segment += 1) {
      if (!combat.damage.left.includes(segment)) {
        context.fillRect(2 * cellX, (state.playerY + segment) * cellY, cellX * 2, cellY);
      }
    }
    context.fillStyle = "#ff4fd8";
    for (let segment = 0; segment < 5; segment += 1) {
      if (!combat.damage.right.includes(segment)) {
        context.fillRect((state.width - 4) * cellX, (state.opponentY + segment) * cellY, cellX * 2, cellY);
      }
    }
    context.fillStyle = "#78f7a4";
    if (combat.shields.left) context.fillRect(6 * cellX, state.playerY * cellY, cellX * 0.8, cellY * 5);
    if (combat.shields.right) context.fillRect((state.width - 7) * cellX, state.opponentY * cellY, cellX * 0.8, cellY * 5);
    for (const rocket of combat.rockets) {
      context.fillStyle = rocket.owner === "left" ? "#39e7ff" : "#ff4fd8";
      const width = cellX * 1.8;
      context.fillRect(rocket.x * cellX - (rocket.vx < 0 ? width : 0), rocket.y * cellY, width, cellY * 0.65);
      context.fillStyle = "#ffe66b";
      context.fillRect(rocket.x * cellX - (rocket.vx > 0 ? cellX * 0.5 : 0), rocket.y * cellY + cellY * 0.18, cellX * 0.5, cellY * 0.3);
    }
    context.fillStyle = "#ffe66b";
    context.fillRect(state.ballX * cellX, state.ballY * cellY, cellX * 1.6, cellY * 0.8);
  }
  if (state.mode === "paused") {
    context.fillStyle = "#f7fbff";
    context.fillText("WAITING FOR PLAYER", canvas.width / 2, canvas.height / 2);
  }
}

function frame(now: number): void {
  const elapsed = Math.min(0.05, (now - previous) / 1000);
  previous = now;
  if (isMultiplayer() && networkTarget) {
    const blend = Math.min(1, elapsed * 24);
    game = {
      ...networkTarget,
      ballX: game.ballX + (networkTarget.ballX - game.ballX) * blend,
      ballY: game.ballY + (networkTarget.ballY - game.ballY) * blend,
      playerY: role === "left" && held ? game.playerY : game.playerY + (networkTarget.playerY - game.playerY) * blend,
      opponentY: role === "right" && held ? game.opponentY : game.opponentY + (networkTarget.opponentY - game.opponentY) * blend,
    };
    if (held && role === "left") game = movePaddle(game, "player", held, PADDLE_SPEED * elapsed);
    if (held && role === "right") game = movePaddle(game, "opponent", held, PADDLE_SPEED * elapsed);
  } else if (game.mode === "playing") {
    if (held) game = movePaddle(game, "player", held, PADDLE_SPEED * elapsed);
    const shieldOwner = ballShieldCollision(game, combat, elapsed);
    const result = shieldOwner
      ? loseLife(game, shieldOwner === "left")
      : stepGame(game, elapsed, true, combat.damage);
    game = result.state;
    if (result.events.includes("point")) combat = createCombatState();
    else combat = advanceRockets(combat, game, elapsed);
    for (const event of result.events) audio.effect(event);
    if (result.events.length) syncScreen();
  }
  stateNode.dataset.playerY = String(game.playerY);
  stateNode.dataset.opponentY = String(game.opponentY);
  stateNode.dataset.leftRockets = String(combat.rockets.filter((rocket) => rocket.owner === "left").length);
  stateNode.dataset.rightRockets = String(combat.rockets.filter((rocket) => rocket.owner === "right").length);
  stateNode.dataset.leftShield = String(combat.shields.left);
  stateNode.dataset.rightShield = String(combat.shields.right);
  stateNode.dataset.leftDamage = combat.damage.left.join(",");
  stateNode.dataset.rightDamage = combat.damage.right.join(",");
  draw(game);
  requestAnimationFrame(frame);
}

start.addEventListener("click", () => input("start"));
playJay.addEventListener("click", () => {
  playJay.disabled = true;
  playJay.textContent = "STARTING JAY…";
  send({ type: "play-ai", seq: ++sequence });
});
createRoom.addEventListener("click", async () => {
  createRoom.disabled = true;
  void audio.start();
  try {
    const response = await fetch("/api/rooms", { method: "POST" });
    if (!response.ok) throw new Error("Room creation failed");
    const room = await response.json() as { code: string; joinUrl: string };
    beginRoom(room.code);
  } catch {
    createRoom.disabled = false;
    message.textContent = "COULD NOT CREATE ROOM · TRY AGAIN";
  }
});
copyRoom.addEventListener("click", async () => {
  await navigator.clipboard.writeText(roomLink.value);
  copyRoom.textContent = "COPIED!";
  window.setTimeout(() => { copyRoom.textContent = "COPY LINK"; }, 1600);
});

document.addEventListener("keydown", (event) => {
  const target = event.target;
  if (
    target instanceof HTMLElement
    && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
  ) return;
  if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
    event.preventDefault();
    if (!event.repeat) combatAction(event.key === "ArrowLeft" ? "left" : "right", true);
    return;
  }
  const actions: Record<string, GameInput> = {
    ArrowUp: "up", ArrowDown: "down", Enter: "start", " ": "start", Escape: "exit", p: "pause",
  };
  const action = actions[event.key];
  if (!action) return;
  event.preventDefault();
  if (action === "up") held = -1;
  if (action === "down") held = 1;
  input(action);
});
document.addEventListener("keyup", (event) => {
  if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
    combatAction(event.key === "ArrowLeft" ? "left" : "right", false);
    return;
  }
  if ((event.key === "ArrowUp" && held < 0) || (event.key === "ArrowDown" && held > 0)) {
    held = 0;
    if (isMultiplayer()) sendMove(0);
  }
});
for (const [selector, direction] of [["#up", -1], ["#down", 1]] as const) {
  const button = document.querySelector<HTMLButtonElement>(selector)!;
  button.addEventListener("pointerdown", (event) => {
    event.preventDefault();
    held = direction;
    input(direction < 0 ? "up" : "down");
    button.setPointerCapture(event.pointerId);
  });
  const release = () => {
    if (held === direction) {
      held = 0;
      if (isMultiplayer()) sendMove(0);
    }
  };
  button.addEventListener("pointerup", release);
  button.addEventListener("pointercancel", release);
  button.addEventListener("lostpointercapture", release);
}

function releaseControls(): void {
  held = 0;
  if (isMultiplayer()) sendMove(0);
  combatAction("left", false);
  combatAction("right", false);
}
window.addEventListener("blur", releaseControls);
document.addEventListener("visibilitychange", () => {
  if (document.hidden) releaseControls();
});
for (const [button, direction] of [[leftControl, "left"], [rightControl, "right"]] as const) {
  button.addEventListener("pointerdown", (event) => {
    event.preventDefault();
    combatAction(direction, true);
    button.setPointerCapture(event.pointerId);
  });
  const release = () => combatAction(direction, false);
  button.addEventListener("pointerup", release);
  button.addEventListener("pointercancel", release);
  button.addEventListener("lostpointercapture", release);
}

if (roomCode) {
  roomLink.value = `${location.origin}/?room=${roomCode}`;
  roomPanel.hidden = false;
  createRoom.hidden = true;
  title.textContent = `ROOM ${roomCode}`;
  message.textContent = "PRESS OK TO CONNECT";
  start.textContent = "OK · JOIN";
}
syncScreen();
requestAnimationFrame(frame);
